/* oxlint-disable anti-slop/no-runtime-typeof */
const PUBLIC_PRICING_TIERS = [
  {
    // Light tier - new entry-level product. The public offer is always discounted,
    // so /pricing shows the same $49 -> $24.50 framing users see at checkout.
    id: 'light',
    type: 'subscription',
    name: 'Light',
    price: 24.5,
    originalPrice: 49,
    productSlug: 'light-plan',
    tagline: 'Build, backtest, and run up to three live trading bots.',
    capacity: {
      liveBots: 3,
      alwaysOnBots: 1,
      backtestHours: 33,
      revisions: 15,
      aiCredits: 24.5,
      support: 'Community',
    },
    bestFor: '',
    features: [
      {
        label: '$24.50 in AI credits per month',
        detail:
          'Shared across the AI Agent and managed AI calls from strategies. Usage with your own provider key is not charged.',
      },
      {
        label: '15 AI strategy revisions per month',
        detail: 'Iterate prompts up to 15 times monthly to improve performance.',
      },
      {
        label: '33 backtesting hours per month',
        detail:
          'About 2,000 minutes of historical sims each billing cycle to validate ideas before going live.',
      },
      {
        label: 'Run up to 3 live bots at a time',
        detail:
          'Includes one always-on bot. Additional capacity is for eligible low-duty schedules.',
      },
      {
        label: 'Broker and market data access',
        detail:
          'Connect supported brokers and market-data sources. Some premium data sources may require their own data subscription.',
      },
      {
        label: 'SEC filings and public market research data',
        detail:
          'Ask the AI Agent for company filings, SEC fundamentals, insider transactions, 13F holdings, macro indicators, Treasury data, labor data, GDP, industry and regional economic data, funding data, and futures positioning.',
      },
      { label: 'Strategy marketplace access', detail: 'Browse and use marketplace strategies.' },
      {
        label: 'Community support',
        detail: 'Get ideas, examples, and product updates from the BotSpot community.',
      },
    ],
    cta: 'Start with Light',
    useCartCheckout: false,
    ctaLink: '/checkout/light-plan',
    featured: false,
    theme: 'purple',
    discountPercent: 50,
  },
  {
    id: 'starter',
    type: 'subscription',
    name: 'Starter',
    price: 49.5,
    originalPrice: 99,
    productSlug: 'starter-plan',
    tagline: 'More backtesting and room to run five live bots.',
    capacity: {
      liveBots: 5,
      alwaysOnBots: 2,
      backtestHours: 200,
      revisions: 100,
      aiCredits: 49.5,
      support: 'Community',
    },
    bestFor: '',
    features: [
      {
        label: 'Everything in Light',
        detail: 'Includes the core features below. Starter limits replace Light limits.',
      },
      {
        label: '$49.50 in AI credits per month',
        detail:
          'Shared across the AI Agent and managed AI calls from strategies. Usage with your own provider key is not charged.',
      },
      {
        label: '100 AI strategy revisions per month',
        detail: 'Iterate prompts up to 100 times monthly to improve performance.',
      },
      {
        label: '200 backtesting hours per month',
        detail: 'Use 200 hours of historical sims each month to validate ideas.',
      },
      {
        label: 'Run up to 5 live bots at a time',
        detail:
          'Includes two always-on bots. Additional capacity is for eligible low-duty schedules.',
      },
    ],
    cta: 'Get Started',
    // Starter uses StarterCheckoutPage for the Light-vs-Starter checkout experiment.
    // This centralizes all starter-paywall purchases through the same bucketing surface.
    useCartCheckout: false,
    ctaLink: '/checkout/starter-plan?product=starter-plan',
    featured: true,
    theme: 'purple',
    discountPercent: 50,
  },
  {
    id: 'pro',
    type: 'subscription',
    name: 'Pro',
    price: 199,
    productSlug: 'pro-plan',
    tagline: 'Live Algo Lab, support calls, and more running bots.',
    capacity: {
      liveBots: 8,
      alwaysOnBots: 5,
      backtestHours: 500,
      revisions: 300,
      aiCredits: 199,
      support: '2 calls + live lab',
    },
    bestFor: '',
    features: [
      {
        label: 'Everything in Starter',
        detail: 'Includes the core features below. Pro limits replace Starter limits.',
      },
      {
        label: '$199 in AI credits per month',
        detail:
          'Shared across the AI Agent and managed AI calls from strategies. Usage with your own provider key is not charged.',
      },
      {
        label: '300 AI strategy revisions per month',
        detail: 'Triple the Starter revision room to refine bots faster.',
      },
      {
        label: '500 backtesting hours per month',
        detail: 'More compute to stress-test and harden strategies.',
      },
      {
        label: 'Run up to 8 live bots at a time',
        detail:
          'Includes five always-on bots. Additional capacity is for eligible low-duty schedules.',
      },
      {
        label: '2 support calls per month',
        detail: 'Two one-on-one expert sessions for guidance and troubleshooting.',
      },
      {
        label: 'Live Algo Lab every Tuesday',
        detail: 'Join Rob live, influence what gets built, and get help when you are stuck.',
        link: { to: '/live-algo-lab', text: 'View Live Algo Lab' },
      },
      {
        label: 'Live Algo Lab replay archive',
        detail:
          'Catch up on past builds and revisit the bots, prompts, and decisions from each session.',
        link: { to: '/live-algo-lab', text: 'View Live Algo Lab' },
      },
    ],
    cta: 'Unlock Pro',
    useCartCheckout: true,
    featured: false,
    theme: 'purple',
  },
  {
    id: 'all-access',
    type: 'subscription',
    name: 'All Access',
    price: 399,
    productSlug: 'all-access-plan',
    tagline: 'Highest limits, faster help, and deeper reviews.',
    capacity: {
      liveBots: 12,
      alwaysOnBots: 10,
      backtestHours: 2000,
      revisions: 500,
      aiCredits: 399,
      support: '4 calls + priority',
    },
    bestFor: '',
    features: [
      {
        label: 'Everything in Pro',
        detail: 'Includes the core features below. All Access limits replace Pro limits.',
      },
      {
        label: '$399 in AI credits per month',
        detail:
          'Shared across the AI Agent and managed AI calls from strategies. Usage with your own provider key is not charged.',
      },
      {
        label: '500 AI strategy revisions per month',
        detail: 'High-volume iteration headroom for teams and power users.',
      },
      {
        label: '2000 backtesting hours per month',
        detail: 'Enterprise-level backtesting capacity for heavier testing.',
      },
      {
        label: 'Run up to 12 live bots at a time',
        detail:
          'Includes ten always-on bots. Additional capacity is for eligible low-duty schedules.',
      },
      {
        label: '4 support calls per month',
        detail: 'Four dedicated expert sessions for planning and optimization.',
      },
      {
        label: 'Live Algo Lab plus priority help',
        detail:
          'Join the Tuesday lab and get faster attention for strategy questions during live sessions and follow-up.',
        link: { to: '/live-algo-lab', text: 'View Live Algo Lab' },
      },
      {
        label: 'Priority setup help',
        detail: 'Faster help getting accounts, bots, and workflows set up.',
      },
      {
        label: 'Strategy review sessions',
        detail: 'Expert review sessions to refine high-usage strategies.',
      },
    ],
    cta: 'Go All Access',
    useCartCheckout: true,
    featured: false,
    theme: 'purple',
  },
  {
    id: 'business',
    type: 'enterprise',
    name: 'Business',
    priceLabel: 'Custom',
    tagline: 'Custom pricing, SLAs, and a dedicated account manager',
    bestFor: 'Teams needing custom capacity, SLAs, and connectors',
    features: [
      { label: 'Bots & backtesting', detail: 'Custom capacity sized to your team and workloads.' },
      {
        label: 'Account manager',
        detail: 'Dedicated owner for onboarding, rollouts, and escalations.',
      },
      {
        label: 'Support & SLAs',
        detail: 'Custom response times and channels to fit your ops needs.',
      },
      {
        label: 'Security & compliance',
        detail: 'Custom reviews, DPAs, and audit support to match governance.',
      },
      { label: 'Broker/venue expansion', detail: 'Custom connectors or venues added on request.' },
    ],
    cta: 'Talk to Our Team',
    ctaLink: '/enterprise',
    isExternal: false,
    theme: 'purple',
  },
  {
    id: 'bootcamp-live',
    type: 'course',
    name: 'Live AI Bootcamp',
    priceLabel: 'See course pricing',
    tagline: '5-week live cohort with recordings, projects, and Q&A.',
    bestFor: 'Builders who want live help and accountability',
    features: [
      {
        label: 'Live sessions and Q&A',
        detail: 'Join the class live, ask questions, and get help while you build.',
      },
      {
        label: 'Recordings and materials included',
        detail: 'Live buyers get the recordings, resources, and course portal access.',
      },
      {
        label: 'Self-paced purchase credits toward live',
        detail:
          'If someone starts self-paced first, what they paid can be applied toward a live seat later.',
      },
    ],
    cta: 'Compare Bootcamp options',
    ctaLink: '/ai-bot-builder-bootcamp',
    featured: false,
    theme: 'purple',
  },
  {
    id: 'bootcamp-self-paced',
    type: 'course',
    name: 'Self-Paced AI Bootcamp',
    price: 499,
    originalPrice: 997,
    productSlug: 'ai-bootcamp-self-paced',
    planKey: 'self-paced-launch',
    tagline: 'Start today with the Bootcamp recordings and materials.',
    bestFor: 'Builders who want the course without live sessions',
    features: [
      {
        label: 'Launch price $499',
        detail: 'Normal price is $997, so the current launch savings is $498.',
      },
      {
        label: 'Bootcamp recording library',
        detail: 'Watch the course recordings on your own schedule.',
      },
      {
        label: 'Course resources and materials',
        detail: 'Use available recordings, summaries, and course resources as they are published.',
      },
      {
        label: '30 days of Pro access',
        detail: 'Use BotSpot Pro while you work through the recordings.',
      },
      {
        label: 'Upgrade credit toward live',
        detail: 'Apply the self-paced purchase amount toward a future live Bootcamp seat.',
      },
    ],
    cta: 'Start self-paced',
    useCartCheckout: true,
    featured: false,
    theme: 'purple',
    discountPercent: 50,
  },
];

function formatPublicPrice(price) {
  if (typeof price === 'number') {
    return `$${price.toLocaleString('en-US', {
      minimumFractionDigits: price % 1 ? 2 : 0,
      maximumFractionDigits: 2,
    })}`;
  }
  return price ? `$${price}` : '';
}

function pricePeriodForTier(tier) {
  if (tier.priceLabel) return '';
  if (tier.type === 'subscription') return 'per month';
  if (tier.type === 'course') return 'one-time';
  return '';
}

function publicPricingSummary(tier) {
  const period = pricePeriodForTier(tier);
  const currentPrice = tier.priceLabel || formatPublicPrice(tier.price);
  const priceText = period ? `${currentPrice} ${period}` : currentPrice;
  return {
    id: tier.id,
    name: tier.name,
    type: tier.type,
    price: priceText,
    originalPrice: tier.originalPrice ? formatPublicPrice(tier.originalPrice) : undefined,
    discountPercent: tier.discountPercent,
    tagline: tier.tagline,
    bestFor: tier.bestFor || undefined,
    capacity: tier.capacity,
    features: tier.features.map((feature) => feature.label),
    featureDetails: tier.features.map((feature) => ({
      label: feature.label,
      detail: feature.detail,
    })),
    cta: tier.cta,
    ctaLink: tier.ctaLink,
    productSlug: tier.productSlug,
    planKey: tier.planKey,
  };
}

function effectivePlanAmount(plan) {
  return typeof plan?.discountedAmount === 'number' ? plan.discountedAmount : plan?.amount;
}

function isMonthlyPlan(plan) {
  return plan?.interval === 'month' && Number(plan?.intervalCount || 1) === 1;
}

function isAnnualPlan(plan) {
  return (
    (plan?.interval === 'year' && Number(plan?.intervalCount || 1) === 1) ||
    (plan?.interval === 'month' && Number(plan?.intervalCount) === 12)
  );
}

function findCatalogPlan(product, billingPeriod) {
  const plans = Array.isArray(product?.plans) ? product.plans : [];
  const matches = plans.filter(billingPeriod === 'annual' ? isAnnualPlan : isMonthlyPlan);

  if (billingPeriod === 'annual') {
    return matches.find((plan) => plan.planKey === 'annual') || matches[0];
  }

  return matches.find((plan) => plan.default) || matches[0];
}

function validateAnnualPricing(product) {
  const monthlyPlan = findCatalogPlan(product, 'monthly');
  const annualPlan = findCatalogPlan(product, 'annual');
  const monthlyAmount = effectivePlanAmount(monthlyPlan);
  const annualAmount = effectivePlanAmount(annualPlan);
  const monthlyBaseAmount = monthlyPlan?.amount;
  const annualBaseAmount = annualPlan?.amount;

  if (
    !monthlyPlan ||
    !annualPlan ||
    typeof monthlyAmount !== 'number' ||
    typeof annualAmount !== 'number' ||
    typeof monthlyBaseAmount !== 'number' ||
    typeof annualBaseAmount !== 'number' ||
    monthlyAmount <= 0 ||
    monthlyBaseAmount <= 0 ||
    annualBaseAmount !== monthlyBaseAmount * 10 ||
    annualAmount !== monthlyAmount * 10 ||
    (monthlyPlan.currency && annualPlan.currency && monthlyPlan.currency !== annualPlan.currency)
  ) {
    throw new Error(`Commercial catalog annual pricing is incomplete for ${product?.slug}`);
  }

  return {
    monthlyPlan,
    annualPlan,
    monthlyAmount,
    annualAmount,
    monthlyBaseAmount,
    annualBaseAmount,
  };
}

function applyCheckoutCatalogToPricingTiers(tiers, catalog, billingPeriod = 'monthly') {
  if (billingPeriod !== 'monthly' && billingPeriod !== 'annual') {
    throw new Error(`Unsupported billing period: ${billingPeriod}`);
  }

  const products = new Map(
    (Array.isArray(catalog?.products) ? catalog.products : [])
      .filter((product) => product && typeof product.slug === 'string')
      .map((product) => [product.slug, product]),
  );

  return tiers.map((tier) => {
    if (tier.type !== 'subscription' || !tier.productSlug) return tier;
    const product = products.get(tier.productSlug);
    const annualPricing = billingPeriod === 'annual' ? validateAnnualPricing(product) : null;
    const plan = annualPricing?.annualPlan || findCatalogPlan(product, 'monthly');
    const capacity = product?.capacity;
    if (
      !product ||
      !plan ||
      typeof plan.amount !== 'number' ||
      !capacity ||
      capacity.monthlyAiRetailCreditUsd == null ||
      capacity.backtestMinutesPerMonth == null ||
      capacity.strategyRevisionsPerMonth == null ||
      capacity.botLimit == null
    ) {
      throw new Error(`Commercial catalog data is incomplete for ${tier.productSlug}`);
    }

    const aiCredits = Number(capacity.monthlyAiRetailCreditUsd);
    const priceCents = effectivePlanAmount(plan);
    const originalPrice =
      billingPeriod === 'annual'
        ? priceCents < plan.amount
          ? plan.amount / 100
          : (annualPricing.monthlyBaseAmount * 12) / 100
        : typeof plan.discountedAmount === 'number'
          ? plan.amount / 100
          : undefined;
    const discountPercent = originalPrice
      ? Math.round((1 - priceCents / 100 / originalPrice) * 100)
      : undefined;
    const backtestHours = capacity.backtestMinutesPerMonth / 60;
    const liveBots = Number(capacity.botLimit);
    const alwaysOnBots = Number(capacity.alwaysOnBotLimit ?? capacity.botLimit);
    const revisions = Number(capacity.strategyRevisionsPerMonth);
    const replaceCommercialLabel = (feature) => {
      if (/AI credits per month/i.test(feature.label)) {
        return { ...feature, label: `${formatPublicPrice(aiCredits)} in AI credits per month` };
      }
      if (/AI strategy revisions per month/i.test(feature.label)) {
        return {
          ...feature,
          label: `${revisions.toLocaleString('en-US')} AI strategy revisions per month`,
        };
      }
      if (/backtesting hours per month/i.test(feature.label)) {
        return {
          ...feature,
          label: `${backtestHours.toLocaleString('en-US', {
            maximumFractionDigits: 1,
          })} backtesting hours per month`,
        };
      }
      if (/Run up to .* live bots? at a time/i.test(feature.label)) {
        return {
          ...feature,
          label: `Run up to ${liveBots} live ${liveBots === 1 ? 'bot' : 'bots'} at a time`,
          detail: `Includes up to ${alwaysOnBots} always-on ${alwaysOnBots === 1 ? 'bot' : 'bots'}. Additional capacity is for eligible low-duty schedules.`,
        };
      }
      return feature;
    };

    return {
      ...tier,
      // Catalog names are operational labels and can be slug-shaped. Keep the
      // curated public plan name while catalog remains source of commercial truth.
      name: tier.name,
      price: priceCents / 100,
      originalPrice,
      discountPercent,
      billingPeriod,
      pricePeriod: billingPeriod === 'annual' ? 'year' : 'month',
      monthlyEquivalentPrice: billingPeriod === 'annual' ? priceCents / 1200 : undefined,
      planKey: plan.planKey,
      ctaLink: `/checkout?product=${encodeURIComponent(product.slug)}&planKey=${encodeURIComponent(plan.planKey)}`,
      useCartCheckout: true,
      capacity: {
        ...tier.capacity,
        liveBots,
        alwaysOnBots,
        backtestHours,
        revisions,
        aiCredits,
      },
      features: tier.features.map(replaceCommercialLabel),
    };
  });
}

function checkoutCatalogSupportsBillingPeriod(tiers, catalog, billingPeriod) {
  try {
    applyCheckoutCatalogToPricingTiers(tiers, catalog, billingPeriod);
    return true;
  } catch {
    return false;
  }
}

const CORE_SUBSCRIPTION_PLANS = PUBLIC_PRICING_TIERS.filter((tier) =>
  ['light', 'starter', 'pro', 'all-access'].includes(tier.id),
);
const BUSINESS_PRICING_PLAN = PUBLIC_PRICING_TIERS.find((tier) => tier.id === 'business');
const EDUCATION_PRICING_PLANS = PUBLIC_PRICING_TIERS.filter((tier) =>
  ['bootcamp-self-paced', 'bootcamp-live'].includes(tier.id),
);

module.exports = {
  BUSINESS_PRICING_PLAN,
  CORE_SUBSCRIPTION_PLANS,
  EDUCATION_PRICING_PLANS,
  PUBLIC_PRICING_TIERS,
  formatPublicPrice,
  publicPricingSummary,
  applyCheckoutCatalogToPricingTiers,
  checkoutCatalogSupportsBillingPeriod,
};
